import { useEffect, useState } from "react";
import axios from "axios";
import { ChefHat, Plus, Loader2, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("fr-FR");

const RestaurantCard = ({ r, onOpen }) => (
  <button type="button" onClick={() => onOpen(r.id)} className="shrink-0 w-48 text-left bg-[#1E293B] border border-white/5 rounded-[1.5rem] overflow-hidden hover:border-[#D4AF37]/40 transition-colors" data-testid={`food-restaurant-${r.id}`}>
    <div className={`relative h-24 bg-[#0F172A] ${r.is_open ? "" : "opacity-50 grayscale"}`}>
      {r.image_url && <img src={r.image_url} alt="" className="w-full h-full object-cover" />}
      {r.logo_url && <img src={r.logo_url} alt="" className="absolute -bottom-4 left-3 w-10 h-10 rounded-full object-cover ring-2 ring-[#1E293B]" data-testid={`food-restaurant-logo-${r.id}`} />}
    </div>
    <div className="p-3 pt-5">
      <p className="font-black text-sm truncate">{r.name}</p>
      <p className="text-[10px] text-slate-400 truncate">{r.cuisine || "Restaurant"} · ★ {r.rating} · {r.eta_min}-{r.eta_max} min</p>
      {!r.is_open && <p className="text-[10px] font-black uppercase text-rose-300 mt-1" data-testid={`food-restaurant-closed-${r.id}`}>Fermé · {r.today_hours}</p>}
    </div>
  </button>
);

const MenuDish = ({ d, onAdd, adding, closed }) => (
  <div className="flex gap-3 bg-[#1E293B] border border-white/5 rounded-2xl p-3" data-testid={`menu-dish-${d.id}`}>
    <img src={d.image_url} alt={d.name} className="w-20 h-20 rounded-xl object-cover shrink-0" />
    <div className="flex-1 min-w-0">
      {d.is_featured && <span className="inline-block mb-1 text-[9px] font-black uppercase tracking-widest bg-[#D4AF37] text-[#0F172A] rounded-full px-2 py-0.5" data-testid={`dish-of-day-badge-${d.id}`}>Plat du jour</span>}
      <p className="font-black text-sm">{d.name}</p>
      {d.description && <p className="text-[11px] text-slate-400 line-clamp-2">{d.description}</p>}
      <p className="text-[#D4AF37] font-black text-sm mt-1">{fmt(d.price)} FCFA</p>
    </div>
    <button type="button" onClick={() => onAdd(d)} disabled={closed || adding === d.id} title={closed ? "Restaurant fermé" : "Ajouter au panier"} className="self-center w-10 h-10 rounded-full bg-[#D4AF37] text-[#0F172A] flex items-center justify-center disabled:opacity-30" data-testid={`menu-add-${d.id}`}>
      {adding === d.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
    </button>
  </div>
);

const RestaurantMenu = ({ menu, onBack, onAdd, adding }) => (
  <section className="px-6 mb-8 space-y-5" data-testid="food-restaurant-menu">
    <button type="button" onClick={onBack} className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-white/60" data-testid="food-menu-back-btn">
      <ArrowLeft size={14} /> Tous les restaurants
    </button>
    <h2 className="text-xl font-black" data-testid="food-menu-restaurant-name">{menu.restaurant.name}</h2>
    {!menu.restaurant.is_open && (
      <p className="rounded-2xl bg-rose-500/10 border border-rose-400/30 text-rose-200 text-xs font-bold px-4 py-3" data-testid="food-menu-closed-banner">
        Fermé pour le moment ({menu.restaurant.today_hours}). Vous pouvez consulter le menu ; les commandes reprendront à l'ouverture.
      </p>
    )}
    {menu.categories.map((c) => (
      <div key={c.id} className="space-y-3" data-testid={`food-menu-category-${c.id}`}>
        <h3 className="text-xs font-black uppercase tracking-[0.25em] text-[#D4AF37]">{c.name}</h3>
        {c.dishes.map((d) => <MenuDish key={d.id} d={d} onAdd={onAdd} adding={adding} closed={!menu.restaurant.is_open} />)}
      </div>
    ))}
  </section>
);

const DishesOfDay = ({ dishes, onAdd, adding }) => (
  <section className="px-6 mb-6 space-y-3" data-testid="food-dishes-of-day">
    <h2 className="text-xs font-black uppercase tracking-[0.25em] text-[#D4AF37]">Plats du jour</h2>
    {dishes.map((d) => (
      <div key={d.id}>
        <p className="text-[10px] text-slate-400 mb-1">{d.restaurant_name}{!d.is_open && ` · Fermé (${d.today_hours})`}</p>
        <MenuDish d={{ ...d, is_featured: true }} onAdd={onAdd} adding={adding} closed={!d.is_open} />
      </div>
    ))}
  </section>
);

// Section Food : plats du jour, restaurants partenaires, puis menu par catégories (plats disponibles)
export const PartnerRestaurants = ({ onAdd, adding }) => {
  const [list, setList] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [q, setQ] = useState("");
  const [cuisine, setCuisine] = useState("");
  const [featured, setFeatured] = useState([]);
  const [menu, setMenu] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      axios.get(`${API}/food/restaurants`, { params: { q: q || undefined, cuisine: cuisine || undefined } })
        .then((r) => { setList(r.data.restaurants || []); setCuisines(r.data.cuisines || []); }).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q, cuisine]);

  useEffect(() => {
    axios.get(`${API}/food/featured`).then((r) => setFeatured(r.data.dishes || [])).catch(() => {});
    const rid = new URLSearchParams(window.location.search).get("restaurant");
    if (rid) open(rid);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (id) => axios.get(`${API}/food/restaurants/${id}/menu`).then((r) => setMenu(r.data)).catch(() => setMenu(null));

  if (menu) return <RestaurantMenu menu={menu} onBack={() => setMenu(null)} onAdd={onAdd} adding={adding} />;
  return (
    <>
      {featured.length > 0 && <DishesOfDay dishes={featured} onAdd={onAdd} adding={adding} />}
      <section className="mb-8" data-testid="food-partner-restaurants">
        <h2 className="px-6 mb-3 text-xs font-black uppercase tracking-[0.25em] text-[#D4AF37] flex items-center gap-2"><ChefHat size={14} /> Restaurants partenaires</h2>
        <div className="px-6 mb-3 space-y-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un restaurant ou une cuisine…" className="w-full h-10 rounded-full bg-[#1E293B] border border-white/10 px-4 text-sm text-white placeholder:text-slate-500" data-testid="food-restaurant-search" />
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {["", ...cuisines].map((c) => (
              <button key={c || "all"} type="button" onClick={() => setCuisine(c)} className={`shrink-0 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest ${cuisine === c ? "bg-[#D4AF37] text-[#0F172A]" : "bg-white/5 text-slate-300"}`} data-testid={`food-cuisine-${c || "all"}`}>
                {c || "Toutes"}
              </button>
            ))}
          </div>
        </div>
        {list.length === 0 ? <p className="px-6 text-xs text-slate-400" data-testid="food-restaurants-empty">Aucun restaurant ne correspond.</p> : (
          <div className="px-6 flex gap-3 overflow-x-auto no-scrollbar pb-2">
            {list.map((r) => <RestaurantCard key={r.id} r={r} onOpen={open} />)}
          </div>
        )}
      </section>
    </>
  );
};
